/**
 * Device fingerprinting utilities for fraud prevention
 * Generates unique device fingerprints based on browser and device characteristics
 */

export interface DeviceFingerprint {
  fingerprint: string;
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  additionalMetrics: Record<string, any>;
}

/**
 * Generate a comprehensive device fingerprint
 */
export function generateDeviceFingerprint(): DeviceFingerprint {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Canvas fingerprinting
  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillText("PayStreem Device Fingerprint", 2, 2);
  }
  const canvasFingerprint = canvas.toDataURL();

  // WebGL fingerprinting
  const gl =
    canvas.getContext("webgl") ||
    (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
  const webglVendor = gl ? gl.getParameter(gl.VENDOR) : "unknown";
  const webglRenderer = gl ? gl.getParameter(gl.RENDERER) : "unknown";

  // Audio context fingerprinting
  let audioFingerprint = "unknown";
  try {
    const audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(10000, audioContext.currentTime);
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);

    oscillator.connect(analyser);
    analyser.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(0);

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(frequencyData);
    audioFingerprint = Array.from(frequencyData.slice(0, 10)).join(",");

    oscillator.stop();
    audioContext.close();
  } catch (e) {
    // Audio fingerprinting failed, use fallback
  }

  // Hardware concurrency and memory
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const deviceMemory = (navigator as any).deviceMemory || 0;

  // Collect all metrics
  const metrics = {
    userAgent: navigator.userAgent,
    screenResolution: `${screen.width}x${screen.height}`,
    screenDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    languages: navigator.languages?.join(",") || "",
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || "unknown",
    hardwareConcurrency,
    deviceMemory,
    webglVendor,
    webglRenderer,
    canvas: canvasFingerprint.slice(-50), // Last 50 chars to keep fingerprint manageable
    audio: audioFingerprint,
    touchSupport: "ontouchstart" in window,
    indexedDbSupport: !!window.indexedDB,
    localStorageSupport: !!window.localStorage,
    sessionStorageSupport: !!window.sessionStorage,
    webRtcSupport: !!(window as any).RTCPeerConnection,
  };

  // Create fingerprint hash
  const fingerprintString = Object.values(metrics).join("|");
  const fingerprint = hashString(fingerprintString);

  return {
    fingerprint,
    userAgent: navigator.userAgent,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    additionalMetrics: {
      hardwareConcurrency,
      deviceMemory,
      webglVendor,
      webglRenderer,
      touchSupport: "ontouchstart" in window,
      cookieEnabled: navigator.cookieEnabled,
    },
  };
}

/**
 * Simple hash function for creating fingerprint
 */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Detect if the device characteristics seem suspicious
 */
export function detectSuspiciousDevice(fingerprint: DeviceFingerprint): {
  suspicious: boolean;
  reasons: string[];
  trustScore: number;
} {
  const reasons: string[] = [];
  let trustScore = 100;

  // Check for common bot/automation indicators
  if (
    fingerprint.userAgent.includes("HeadlessChrome") ||
    fingerprint.userAgent.includes("PhantomJS") ||
    fingerprint.userAgent.includes("SlimerJS")
  ) {
    reasons.push("Headless browser detected");
    trustScore -= 50;
  }

  // Check for missing expected features
  if (!fingerprint.additionalMetrics.cookieEnabled) {
    reasons.push("Cookies disabled");
    trustScore -= 20;
  }

  // Check for unusual screen resolutions
  const resolution = fingerprint.screenResolution;
  if (resolution === "0x0" || resolution.includes("0")) {
    reasons.push("Invalid screen resolution");
    trustScore -= 30;
  }

  // Check for missing hardware info
  if (fingerprint.additionalMetrics.hardwareConcurrency === 0) {
    reasons.push("No hardware concurrency info");
    trustScore -= 15;
  }

  // Check for webdriver presence
  if ((window as any).webdriver || (navigator as any).webdriver) {
    reasons.push("WebDriver detected");
    trustScore -= 40;
  }

  // Check for common automation frameworks
  if (
    (window as any).phantom ||
    (window as any).callPhantom ||
    (window as any)._phantom ||
    (window as any).__nightmare
  ) {
    reasons.push("Automation framework detected");
    trustScore -= 45;
  }

  trustScore = Math.max(0, Math.min(100, trustScore));

  return {
    suspicious: trustScore < 60,
    reasons,
    trustScore,
  };
}

/**
 * Generate a session ID for tracking
 */
export function generateSessionId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
